import { getLocale } from "@umijs/max";

// API Configuration
export const API_CONFIG = {
  BASE_URL: "https://prod-api.eatsy.vn",
  // BASE_URL: "https://prod-api.eatsy.vn",
  MEDIA_BASE_URL: "https://media.eatsy.vn",
  MEDIA_API_KEY: "upload_key_456789123",
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
} as const;

// Common API endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: "/admin-users/login",
    ME: "/v1/auth/me",
  },
  USERS: {
    BASE: "/users",
    CREATE_ADMIN: "/users/create-admin",
    TOGGLE_STATUS: (id: string) => `/users/${id}/toggle-status`,
    BY_ID: (id: string) => `/users/${id}`,
    ADMIN_PROFILE: (id: string) => `/admin/users/${id}/profile`,
    ADMIN_DELETE: (id: string) => `/admin/users/${id}`,
    ADMIN_ENTITLEMENTS: (id: string) => `/admin/users/${id}/entitlements`,
    NEW_USERS_CHART: "/admin/users/statistics/new-users-chart",
  },
  BRANDS: {
    BASE: "/food-brands",
    ALL: "/food-brands/all",
    SEARCH: "/food-brands/search",
    BY_STATUS: (isActive: boolean) => `/food-brands/status/${isActive}`,
    BY_ID: (id: string) => `/food-brands/${id}`,
  },
  CATEGORIES: {
    BASE: "/food-categories",
    BY_ID: (id: string) => `/food-categories/${id}`,
    TRANSLATIONS: (id: string) => `/food-categories/${id}/translations`,
  },
  DISH_CATEGORIES: {
    BASE: "/dish-categories",
    BY_ID: (id: string) => `/dish-categories/${id}`,
    TRANSLATIONS: (id: string) => `/dish-categories/${id}/translations`,
    BY_PARENT: (parentId: string) => `/dish-categories/parent/${parentId}`,
    // Dishes management in category
    ADD_DISHES: (id: string) => `/dish-categories/${id}/dishes`,
    REMOVE_DISH: (id: string, dishId: string) =>
      `/dish-categories/${id}/dishes/${dishId}`,
    GET_DISHES: (id: string) => `/dish-categories/${id}/dishes`,
  },
  DISH_CATALOGUES: {
    BASE: "/admin/dish-catalogues",
    BY_ID: (id: string) => `/admin/dish-catalogues/${id}`,
    GET_DISHES: (id: string) => `/admin/dish-catalogues/${id}/dishes`,
    ADD_DISHES: (id: string) => `/admin/dish-catalogues/${id}/dishes`,
    REMOVE_DISH: (id: string, dishId: string) =>
      `/admin/dish-catalogues/${id}/dishes/${dishId}`,
    REORDER_DISHES: (id: string) =>
      `/admin/dish-catalogues/${id}/dishes/reorder`,
  },
  FOODS: {
    BASE: "/foods",
    ADMIN: "/foods/admin",
    PACKAGED: "/foods/admin/packaged",
    ADMIN_BY_ID: (id: string) => `/foods/admin/${id}`,
    BY_ID: (id: string) => `/foods/${id}`,
    BY_SLUG: (slug: string) => `/foods/slug/${slug}`,
    BY_BARCODE: (barcode: string) => `/foods/barcode/${barcode}`,
    SEARCH: "/foods/search",
    NUTRITIONAL_RANGE: "/foods/nutritional-range",
    DEACTIVATE: (id: string) => `/foods/${id}/deactivate`,
    UPDATE_POPULAR: (id: string) => `/foods/admin/${id}/popular`,
    TOGGLE_ACTIVE: (id: string) => `/foods/admin/${id}/toggle-active`,
    // Food Tags Management
    TAGS: (foodId: string) => `/foods/admin/${foodId}/tags`,
    // Export
    EXPORT_EXCEL: "/foods/admin/export/excel",
    ES_BULK_INDEX: "/foods/admin/elasticsearch/bulk-index",
  },
  SYSTEM: {
    CONFIG: "/system/config",
    MAINTENANCE: "/admin/system/config/maintenance",
  },
  // Custom Foods (Admin Only)
  CUSTOM_FOODS: {
    BASE: "/custom-foods",
    ADMIN_ALL: "/custom-foods/admin/all",
  },
  // Food Contributions (consumer barcode submissions)
  FOOD_CONTRIBUTIONS: {
    BASE: "/v3/food-contributions",
    BY_ID: (id: string) => `/v3/food-contributions/${id}`,
    REVIEW: (id: string) => `/v3/food-contributions/${id}/review`,
    EXTRACT_NUTRITION: (id: string) =>
      `/v3/food-contributions/${id}/extract-nutrition`,
    USER_LOCKS: "/v3/food-contributions/admin/user-locks",
    USER_LOCK_BY_ID: (userId: string) =>
      `/v3/food-contributions/admin/user-locks/${userId}`,
    VIOLATION_STATS: "/v3/food-contributions/admin/violation-stats",
    /** §19.8 — daily contribution cap (PO 2026-10-27). */
    CONFIG: "/v3/admin/food-contributions/config",
  },
  // Eatsy Coin (admin)
  EATSY_COIN: {
    STATS: "/v3/admin/coins/stats",
    WALLETS: "/v3/admin/coins/wallets",
    LEDGER: "/v3/admin/coins/ledger",
    ADJUST: "/v3/admin/coins/adjust",
    CONFIG: "/v3/admin/coins/config",
    REDEMPTIONS: "/v3/admin/coins/redemptions",
  },
  // Food Tags
  FOOD_TAGS: {
    BASE: "/food-tags",
    BY_ID: (id: string) => `/food-tags/${id}`,
    BY_CATEGORY: (category: string) => `/food-tags/category/${category}`,
    CATEGORIES: "/food-tags/categories",
    ADMIN_FOODS: (tagId: string) => `/food-tags/admin/${tagId}/foods`,
    TAG_FOODS: (tagId: string) => `/food-tags/${tagId}/foods`,
  },
  // Exercise Activity
  EXERCISE_CATEGORIES: {
    BASE: "/exercise-categories",
    BY_ID: (id: string) => `/exercise-categories/${id}`,
    BY_PARENT: (parentId: string) => `/exercise-categories/parent/${parentId}`,
    ROOT: "/exercise-categories/root/categories",
    TRANSLATIONS: (id: string) => `/exercise-categories/${id}/translations`,
  },
  EXERCISES: {
    BASE: "/exercises",
    ADMIN: "/exercises/admin",
    BY_ID: (id: string) => `/exercises/${id}`,
    BY_CATEGORY: (categoryId: string) => `/exercises/category/${categoryId}`,
    // Export
    EXPORT_EXCEL: "/exercises/export/excel",
    // Statistics
    NEW_EXERCISE_LOGS_CHART:
      "/admin/exercise/statistics/new-exercise-logs-chart",
  },
  EXERCISE_DIFFICULTY_LEVELS: {
    BASE: "/exercise-difficulty-levels",
    BY_ID: (id: string) => `/exercise-difficulty-levels/${id}`,
    BY_EXERCISE: (exerciseId: string) =>
      `/exercise-difficulty-levels/exercise/${exerciseId}`,
  },
  // Food Serving Sizes
  FOOD_SERVING_SIZES: {
    BASE: "/food-serving-sizes",
    BY_ID: (id: string) => `/food-serving-sizes/${id}`,
    BY_FOOD_ID: (foodId: string) => `/food-serving-sizes/food/${foodId}`,
  },
  // Nutrition Analytics
  NUTRITION: {
    USER_ANALYTICS: (userId: string) => `/v3/admin/nutrition/users/${userId}`,
    NEW_MEALS_CHART: "/v3/admin/nutrition/statistics/new-meals-chart",
    CHECK_IN_HISTORY: (userId: string) =>
      `/nutrition-tracking/check-in/admin/history/${userId}`,
  },
  // Weight & Health Tracking
  WEIGHT_HEALTH_TRACKING: {
    USER_WEIGHT_HISTORY: (userId: string) =>
      `/admin/weight-health-tracking/users/${userId}/weight-history`,
    NEW_WEIGHT_LOGS_CHART:
      "/admin/weight-health-tracking/statistics/new-weight-logs-chart",
    WEIGHT_TREND_CHART: (userId: string) => `/weight-trend/admin/chart/${userId}`,
    DAILY_HISTORY: (userId: string) =>
      `/weight-trend/admin/daily-history/${userId}`,
  },
  // User Onboarding Preferences
  USER_ONBOARDING_PREFERENCES: {
    ADMIN_BY_USER_ID: (userId: string) =>
      `/v3/user-onboarding-preferences/admin/${userId}`,
  },
  // Smart Recommendations (SRS v11 §9.18 Admin Phase 1)
  RECOMMENDATIONS: {
    SYSTEM_STATS: "/v3/admin/recommendations/system-stats",
    SESSIONS: "/v3/admin/recommendations/sessions",
    SESSION_BY_ID: (sessionId: string) =>
      `/v3/admin/recommendations/sessions/${sessionId}`,
    SESSION_ITEMS: (sessionId: string) =>
      `/v3/admin/recommendations/sessions/${sessionId}/items`,
    SESSION_FEEDBACK: (sessionId: string) =>
      `/v3/admin/recommendations/sessions/${sessionId}/feedback`,
    USER_SESSIONS: (userId: string) =>
      `/v3/admin/recommendations/users/${userId}/sessions`,
    ALLERGEN_COVERAGE: "/v3/admin/recommendations/allergen-coverage",
    ALLERGEN_MAPPINGS: "/v3/admin/recommendations/allergen-mappings",
    ALLERGEN_MAPPING_BY_ID: (id: string) =>
      `/v3/admin/recommendations/allergen-mappings/${id}`,
    ALLERGEN_MAPPING_VERIFY: (id: string) =>
      `/v3/admin/recommendations/allergen-mappings/${id}/verify`,
    ALLERGEN_MAPPINGS_BULK_IMPORT:
      "/v3/admin/recommendations/allergen-mappings/bulk-import",
    ALLERGEN_MAPPINGS_EXPORT_UNCLASSIFIED:
      "/v3/admin/recommendations/allergen-mappings/export-unclassified",
    TIME_PERIOD_CONFIG: "/v3/admin/recommendations/time-period-config",
    TIME_PERIOD_ACTIVE_USERS: (timePeriod: string) =>
      `/v3/admin/recommendations/time-period-config/${timePeriod}/active-users`,
    ACCESS_CONFIG: "/v3/admin/recommendations/access-config",
    ALGORITHM_CONFIG: "/v3/admin/recommendations/algorithm-config",
    CATALOG_SOURCES: "/v3/admin/recommendations/catalog-sources",
    CATALOG_SOURCE_BY_ID: (id: string) =>
      `/v3/admin/recommendations/catalog-sources/${id}`,
    REPORT_FUNNEL: "/v3/admin/recommendations/reports/funnel",
    REPORT_TIME_PERIOD_MIX: "/v3/admin/recommendations/reports/time-period-mix",
    REPORT_FEEDBACK_TREND: "/v3/admin/recommendations/reports/feedback-trend",
    REPORT_LOGGING_RATE: "/v3/admin/recommendations/reports/logging-rate",
    REPORT_ACCESS_DENIALS: "/v3/admin/recommendations/reports/access-denials",
    REPORT_PREMIUM_SPLIT: "/v3/admin/recommendations/reports/premium-split",
    REPORT_LATENCY: "/v3/admin/recommendations/reports/latency",
    REPORT_REGISTRATION_COHORT:
      "/v3/admin/recommendations/reports/registration-cohort",
  },
  ONBOARDING_CATALOGUES: {
    EATING_STYLES: "/v3/admin/onboarding-catalogues/eating-styles",
    EATING_STYLE_BY_ID: (id: string) =>
      `/v3/admin/onboarding-catalogues/eating-styles/${id}`,
    PROTEIN_LEVELS: "/v3/admin/onboarding-catalogues/protein-levels",
    PROTEIN_LEVEL_BY_ID: (id: string) =>
      `/v3/admin/onboarding-catalogues/protein-levels/${id}`,
    PCF_RATIOS: "/v3/admin/onboarding-catalogues/pcf-ratios",
    PCF_RATIO_BY_ID: (id: string) =>
      `/v3/admin/onboarding-catalogues/pcf-ratios/${id}`,
    REFERRAL_CHANNELS: "/v3/admin/onboarding-catalogues/referral-channels",
    REFERRAL_CHANNEL_BY_ID: (id: string) =>
      `/v3/admin/onboarding-catalogues/referral-channels/${id}`,
    ALLERGEN_PRESETS: "/v3/admin/onboarding-catalogues/allergen-presets",
    ALLERGEN_PRESET_BY_ID: (id: string) =>
      `/v3/admin/onboarding-catalogues/allergen-presets/${id}`,
    FAVORITE_FOOD_GROUPS: "/v3/admin/onboarding-catalogues/favorite-food-groups",
    FAVORITE_FOOD_GROUP_BY_ID: (id: string) =>
      `/v3/admin/onboarding-catalogues/favorite-food-groups/${id}`,
    FAVORITE_FOOD_ITEMS: "/v3/admin/onboarding-catalogues/favorite-food-items",
    FAVORITE_FOOD_ITEM_BY_ID: (id: string) =>
      `/v3/admin/onboarding-catalogues/favorite-food-items/${id}`,
  },
  // Weight Logs
  WEIGHT_LOGS: {
    BASE: "/weight-logs",
  },
  // Step Logs
  STEP_LOGS: {
    BASE: "/step-logs",
  },
  // Health Metrics
  HEALTH_METRICS: {
    LAST_RECORDS: (userId: string) => `/health-metrics/last-records/${userId}`,
    // Detailed health metrics with pagination
    SLEEP_LOGS: "/health-metrics/sleep-logs/admin",
    BLOOD_GLUCOSE_LOGS: "/health-metrics/blood-glucose-logs/admin",
    BLOOD_PRESSURE_LOGS: "/health-metrics/blood-pressure-logs/admin",
    BODY_FAT_LOGS: "/health-metrics/body-fat-logs/admin",
    CHEST_CIRCUMFERENCE_LOGS: "/health-metrics/chest-circumference-logs/admin",
    FASTING_LOGS: "/health-metrics/fasting-logs/admin",
    HIP_CIRCUMFERENCE_LOGS: "/health-metrics/hip-circumference-logs/admin",
    WAIST_CIRCUMFERENCE_LOGS: "/health-metrics/waist-circumference-logs/admin",
  },
  // Nutrition Goals
  NUTRITION_GOALS: {
    BY_USER_ID: (userId: string) => `/nutrition-goals/user/${userId}`,
  },
  // Daily Nutrition Summary
  DAILY_NUTRITION_SUMMARY: {
    ADMIN: "/daily-nutrition-summary/admin",
  },
  // Image Logs (Nutrition Tracking)
  IMAGE_LOGS: {
    ADMIN: "/nutrition-tracking/admin/image-logs",
  },
  // Food Image Enrichment
  FOOD_IMAGE_ENRICHMENT: {
    BASE: "/food-image-enrichment",
    BATCH: "/food-image-enrichment",
    BY_FOOD_ID: (foodId: string) => `/food-image-enrichment/${foodId}`,
    LOGS: "/food-image-enrichment/logs",
    LOG_BY_ID: (id: string) => `/food-image-enrichment/logs/${id}`,
    APPROVE: (id: string) => `/food-image-enrichment/logs/${id}/approve`,
    REJECT: (id: string) => `/food-image-enrichment/logs/${id}/reject`,
    RETRY: (id: string) => `/food-image-enrichment/logs/${id}/retry`,
    PUBLISH: (id: string) => `/food-image-enrichment/logs/${id}/publish`,
  },
  // Food Parsing Logs
  FOOD_PARSING_LOGS: {
    ADMIN: "/food-parsing/admin/parsing-logs",
  },
  // Customer Activities
  CUSTOMER_ACTIVITIES: {
    BY_USER_ID: (userId: string) =>
      `/admin/customer-activities/users/${userId}`,
  },
  // Nutrition Plans
  NUTRITION_PLANS: {
    ADMIN_USER_TEMPLATES: (userId: string) =>
      `/nutrition-plans/admin/users/${userId}/templates`,
    ADMIN_GENERATE_FOR_USER: (userId: string) =>
      `/nutrition-plans/admin/users/${userId}/generate`,
  },
  // User Exercise Logs
  USER_EXERCISE_LOGS: {
    ADMIN_DAILY: "/user-exercise-logs/admin/daily",
  },
  // Meal Streaks
  MEAL_STREAKS: {
    LEADERBOARD: "/meal-streaks/leaderboard",
  },
  // Subscriptions
  SUBSCRIPTIONS: {
    ME: "/subscriptions/me",
    ADMIN_BY_USER_ID: (userId: string) => `/subscriptions/admin/${userId}`,
    NEW_TRIALS_CHART: "/subscriptions/trial/statistics/new-trials-chart",
    ADMIN_UPGRADE: "/subscriptions/admin/upgrade",
    REVENUECAT_SUBSCRIPTIONS: "/subscriptions/revenuecat/subscriptions",
  },
  LANGUAGES: {
    BASE: "/languages",
    BY_ID: (id: string) => `/languages/${id}`,
    TOGGLE_ACTIVE: (id: string) => `/languages/${id}/toggle-active`,
    SET_DEFAULT: (id: string) => `/languages/${id}/set-default`,
  },
  // Water Logs
  WATER_LOGS: {
    NEW_WATER_LOGS_CHART: "/admin/water/statistics/new-water-logs-chart",
  },
  STATISTICS: {
    REVENUE_DASHBOARD: "/v3/admin/statistics/revenue-dashboard",
  },
  FEATURE_SATISFACTION: {
    BASE: "/v3/admin/feature-satisfaction",
    SUMMARY: "/v3/admin/feature-satisfaction/summary",
  },
  // Water Types
  WATER_TYPES: {
    BASE: "/water-types",
    BY_ID: (id: string) => `/water-types/${id}`,
    TRANSLATIONS: (id: string) => `/water-types/${id}/translations`,
  },
  // AI Chat
  AI_CHAT: {
    NEW_CONVERSATIONS_CHART:
      "/admin/ai-chat/statistics/new-conversations-chart",
  },
  // Conversations
  CONVERSATIONS: {
    ADMIN_LIST: "/ai-chat/conversations/admin",
    BY_ID: (id: string) => `/ai-chat/conversations/admin/${id}`,
    MESSAGES: (id: string) => `/ai-chat/conversations/${id}/messages`,
  },
  // User Notifications
  USER_NOTIFICATIONS: {
    BASE: "/user-notifications",
    BY_ID: (id: string) => `/user-notifications/${id}`,
    BY_USER_ID: (userId: string) => `/user-notifications/user/${userId}`,
    ADMIN_SEND_TO_USERS: "/user-notifications/admin/send-to-users",
  },
  // Push Tokens
  PUSH_TOKENS: {
    BY_USER_ID: (userId: string) => `/push-tokens/user/${userId}`,
  },
  // Gift Campaigns
  // Mã kích hoạt Premium (eatsy-backend-v2/docs/GIFT-CODES-GEN3.md)
  GIFT_CODES: {
    LIST: "/v3/admin/gift-codes",
    BATCHES: "/v3/admin/gift-codes/batches",
    REVOKE: (id: string) => `/v3/admin/gift-codes/${id}/revoke`,
  },
  GIFT_CAMPAIGNS: {
    BASE: "/admin/gift-campaigns",
    BY_ID: (id: string) => `/admin/gift-campaigns/${id}`,
  },
  // Gift Orders
  GIFT_ORDERS: {
    ADMIN_LIST: "/gift-orders/admin/list",
    VERIFY: (id: string) => `/gift-orders/admin/${id}/verify`,
    REJECT: (id: string) => `/gift-orders/admin/${id}/reject`,
    UPDATE_STATUS: (id: string) => `/gift-orders/admin/${id}/status`,
  },
  // Scale Orders
  SCALE_ORDERS: {
    ADMIN_LIST: "/scale-orders/admin/list",
    ADMIN_BY_ID: (id: string) => `/scale-orders/admin/${id}`,
    VERIFY: (id: string) => `/scale-orders/admin/${id}/verify`,
    REJECT: (id: string) => `/scale-orders/admin/${id}/reject`,
    UPDATE_STATUS: (id: string) => `/scale-orders/admin/${id}/status`,
    DELETE: (id: string) => `/scale-orders/admin/${id}`,
  },
  // Shop — Products
  SHOP_PRODUCTS: {
    LIST: "/shop/products",
    BY_ID: (id: string) => `/shop/products/${id}`,
    ADMIN_CREATE: "/shop/products/admin",
    ADMIN_UPDATE: (id: string) => `/shop/products/admin/${id}`,
    ADMIN_DELETE: (id: string) => `/shop/products/admin/${id}`,
  },
  // Shop — Orders
  SHOP_ORDERS: {
    ADMIN_LIST: "/shop/orders/admin/list",
    ADMIN_CREATE: "/shop/orders/admin",
    ADMIN_UPDATE: (id: string) => `/shop/orders/admin/${id}`,
    ADMIN_CANCEL: (id: string) => `/shop/orders/admin/${id}/cancel`,
    ADMIN_UPDATE_STATUS: (id: string) => `/shop/orders/admin/${id}/status`,
  },
  // Shop — Shipping Methods
  SHOP_SHIPPING_METHODS: {
    BASE: "/shop/shipping-methods",
    ADMIN_BASE: "/shop/shipping-methods/admin",
    ADMIN_BY_ID: (id: string) => `/shop/shipping-methods/admin/${id}`,
  },
  // Shop — Payment Methods
  SHOP_PAYMENT_METHODS: {
    BASE: "/shop/payment-methods",
    ADMIN_BASE: "/shop/payment-methods/admin",
    ADMIN_BY_ID: (id: string) => `/shop/payment-methods/admin/${id}`,
  },
  // Shop Settings
  SHOP_SETTINGS: {
    ADMIN: "/shop/settings/admin",
  },
  // Vietnam administrative units (provinces / wards)
  VIETNAM_ADMINISTRATIVE: {
    SYNC: "/vn-provinces/sync",
  },
  // Upload
  UPLOAD: {
    IMAGE: "/upload/image",
    IMAGES: "/upload/images",
  },
} as const;

/** Maps UMI locale (e.g. "vi-VN", "en-US") to BCP 47 short code ("vi", "en").
 *  Returns 'vi' when locale is undefined (UMI not yet initialised, test env, SSR). */
const localeToLangCode = (locale: string | undefined): string =>
  locale ? locale.split("-")[0].split("_")[0].toLowerCase() : "vi";

// Common headers
export const getCommonHeaders = (): Record<string, string> => ({
  "Content-Type": "application/json",
  "x-lang": localeToLangCode(getLocale()),
});

// Authentication headers
export const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("accessToken");
  if (!token) {
    throw new Error("No access token found. Please login first.");
  }

  return {
    ...getCommonHeaders(),
    Authorization: `Bearer ${token}`,
  };
};
